import NsContent from 'components/common/ns-content/NsContent';
import NsFormGroup from '@britishgasenergy/react-nucleus/dist/nuc-components/NsFormGroup';
import NsDownload from '@britishgasenergy/react-nucleus/dist/nuc-components/NsDownload';

import { NsPanel } from 'components/common/ns-panel/NsPanel';
import { NsFirstNameInput } from 'components/common/ns-first-name-input/NsFirstNameInput';
import { NsLastNameInput } from 'components/common/ns-last-name-input/NsLastNameInput';
import { NsEmailInput } from 'components/common/ns-email-input/NsEmailInput';
import { NsPhoneInput } from 'components/common/ns-phone-input/NsPhoneInput';
import { NsPostcodeInput } from 'components/common/ns-postcode-input/NsPostcodeInput';
import { NsCheckboxInput } from 'components/common/ns-checkbox-input/NsCheckboxInput';
import { NsInputterValidations } from 'components/common/ns-inputter/NsInputterValidations';
import { NsForm } from 'components/common/ns-form/NsForm';
import { NsPrimaryCta } from 'components/common/ns-primary-cta/NsPrimaryCta';
import { NsTextInput } from 'components/common/ns-text-input/NsTextInput';
import { Error } from 'components/common/error/Error';
import { NsList } from 'components/common/ns-list/NsList';
import { useMaskPII } from 'hooks/useMaskPII';
import NsInputterEvent from '../ns-inputter/NsInputterEvent';

export const testId = 'registrationFormTestId';

type RegistrationFormProps = {
  details: {
    firstName: string;
    lastName: string;
    electricityAccountNumber: string;
    email: string;
    phoneNumber: string;
    address: string;
    postcode: string;
  };
  errors: { message: string }[];
  isLoading: boolean;
  handleGoNext: (event: React.FormEvent<HTMLFormElement>) => void;
  handleChange: (event: NsInputterEvent) => void;
  termsPdf: string;
  termsPdfText: string;
  termsCheckboxLabel: string;
  formOfferName: string;
};

export const RegistrationForm = ({
  details,
  errors,
  isLoading,
  handleGoNext,
  handleChange,
  termsPdf,
  termsPdfText,
  termsCheckboxLabel,
  formOfferName
}: RegistrationFormProps) => {
  const { mask } = useMaskPII();

  return (
    <NsPanel testId={testId}>
      <NsContent>
        <h2>Enter your details</h2>
      </NsContent>

      <NsForm onSubmit={handleGoNext}>
        <NsFormGroup>
          <NsFirstNameInput
            value={details.firstName}
            onChange={handleChange}
            inputAttributes={mask}
          />

          <NsLastNameInput
            value={details.lastName}
            onChange={handleChange}
            inputAttributes={mask}
          />

          <NsTextInput
            label="Electricity account number"
            name="electricityAccountNumber"
            value={details.electricityAccountNumber}
            onChange={handleChange}
            pattern="^(A\d{6,8}|BGX\d{4,15})$"
            validation={[NsInputterValidations.isRequired]}
            helper="Where do I find my electricity account number?"
            tipDetail={
              <>
                <p>
                  You&apos;ll find your account number on any bill or statement.
                  Your account number can be in the following format only:
                </p>
                <NsList>
                  {[
                    <span key={0} className="p-caption">
                      Starting with &apos;A&apos; then followed by minimum of 6
                      digits and maximum 8
                    </span>,
                    <span key={1} className="p-caption">
                      Starting with &apos;BGX&apos; then followed by minimum of
                      4 digits and maximum 15
                    </span>
                  ]}
                </NsList>
                <p>
                  <b>
                    If you have a different type of account number, then your
                    account is not eligible for this {formOfferName}.
                  </b>
                </p>
              </>
            }
          />

          <NsEmailInput
            value={details.email}
            onChange={handleChange}
            inputAttributes={mask}
          />

          <NsPhoneInput
            value={details.phoneNumber}
            onChange={handleChange}
            inputAttributes={mask}
          />

          <NsTextInput
            label="First line of address"
            name="address"
            value={details.address}
            pattern="^[A-Za-z0-9.'#@,%&\/ \-]{0,70}$"
            onChange={handleChange}
            validation={[NsInputterValidations.isRequired]}
          />

          <NsPostcodeInput
            value={details.postcode}
            onChange={handleChange}
            inputAttributes={mask}
          />

          <NsDownload href={termsPdf}>{termsPdfText}</NsDownload>

          <NsCheckboxInput
            name="terms"
            options={[
              {
                label: termsCheckboxLabel,
                value: 'true'
              }
            ]}
            validation={[NsInputterValidations.isRequired]}
          />

          <NsPrimaryCta isLoading={isLoading}>Sign up</NsPrimaryCta>
        </NsFormGroup>

        <Error errors={errors} />
      </NsForm>
    </NsPanel>
  );
};
